import "VSS/LoaderPlugins/Css!WorkItemTracking/Components/DiscussionEditor";

import * as React from "react";
import { css } from "VSSUI/Util";
import { IdentityRef } from "VSS/WebApi/Contracts";
import { ITelemetryContext, HtmlEditor } from "WorkItemTracking/Scripts/Components/HtmlEditor";
import { Component, imageSizeSmall } from "Presentation/Scripts/TFS/Components/IdentityImage";
import { IMessageEntryControl } from "WorkItemTracking/Scripts/Controls/WorkItemForm/DiscussionEditorInterfaces";
import * as WITResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import { Button } from "VSSUI/Button";

const TelemetryContext: ITelemetryContext = { controlName: "DiscussionEditor" };

export interface IDiscussionEditorProps {
    hideAvatar?: boolean;
    currentIdentity?: IdentityRef;
    className?: string;
    onChange?: () => void;
    onFocus?: () => void;
    uploadImageHandler?: (file: File, ignoreAreaPath?: boolean) => Promise<string>;
    placeholder?: string;
    helpText?: string;
    ariaLabel?: string;
    onAddComment?: (newValue: string) => void;
    onCancelComment?: () => void;
}

export interface IDiscussionEditorState {
    isHidden?: boolean;
    height?: number;
    areContolButtonsHidden: boolean;
    isNewCommentActive: boolean;
    isNewCommentEmpty: boolean;
    isNewCommentSaving: boolean;
}

export class DiscussionEditor extends React.PureComponent<IDiscussionEditorProps, IDiscussionEditorState> implements IMessageEntryControl {
    private _control: HtmlEditor;

    constructor(props: IDiscussionEditorProps) {
        super(props);
        this.state = { isHidden: false, height: undefined, areContolButtonsHidden: true, isNewCommentActive: false, isNewCommentEmpty: true, isNewCommentSaving: false };
    }

    public render(): JSX.Element {
        const { className, hideAvatar, currentIdentity, uploadImageHandler, placeholder, helpText, ariaLabel } = this.props;
        return (
            <div className={css("discussion-editor-control", className, { "no-avatar": hideAvatar, "is-hidden": this.state.isHidden })}>
                <div className={"discussion-message-entry"}>
                    {!hideAvatar && (
                        <div className={"discussion-messages-left"}>
                            <Component size={imageSizeSmall} identity={currentIdentity} showProfileCardOnClick={true} />
                        </div>
                    )}
                    <div className={"discussion-messages-right"}>
                        <HtmlEditor
                            htmlContent={""}
                            onChange={this.onNewCommentChange}
                            className={"editor"}
                            uploadImageHandler={(file) =>
                                uploadImageHandler(
                                    file,
                                    /*ignoreAreaPath*/ true
                                )
                            }
                            ref={this._htmlEditorOnRef}
                            mentionsEnabled={true}
                            placeholder={placeholder}
                            helpText={helpText}
                            telemetryContext={TelemetryContext}
                            showChromeBorder={true}
                            ariaLabel={ariaLabel}
                            height={this.state.height}
                            forceDelayLoad={true}
                            onFocus={this.onNewCommentFocus}
                            onBlur={this.onNewCommentBlur}
                            preventShrinkOnBlur={true}
                        />
                        {(this.isNewCommentButtonsAvailable()) && (this.renderButtons())}
                    </div>
                </div>
            </div>
        );
    }

    private isNewCommentButtonsAvailable = (): boolean => {
        return this.state.isNewCommentActive
            && !this.state.areContolButtonsHidden;
    }

    private renderButtons = (): JSX.Element => {
        return (
            <div className={"new-comment-message-button-area"}>
                <Button
                    className={"editor-cancel-button"}
                    text={WITResources.Cancel}
                    disabled={this.state.isNewCommentSaving}
                    onClick={this.handleCancel}
                    ariaLabel={WITResources.Cancel} />
                <Button
                    text={WITResources.Save}
                    primary={true}
                    ariaLabel={WITResources.Save}
                    disabled={this.state.isNewCommentEmpty || this.state.isNewCommentSaving}
                    onClick={this.onUpdateClick} />
            </div>
        );
    };

    private onUpdateClick = (): void => {
        const { onAddComment } = this.props;
        if (!onAddComment) {
            return;
        }

        const htmlContent = this._control.htmlContent;
        this._control?.setEnabled(false);
        this.setState({ isNewCommentSaving: true });

        onAddComment(htmlContent);

        this._control?.setContent("");
        this.setState({ isNewCommentSaving: false, isNewCommentActive: false });
        this._control?.setEnabled(true);
    };

    private handleCancel = (): void => {
        const { onCancelComment } = this.props;
        if (!onCancelComment) {
            return;
        }

        onCancelComment();

        this._control?.setContent("");
        this.setState({ isNewCommentEmpty: true, isNewCommentActive: false });
    };

    public static isHtmlEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        // This is to fix #320022. We dont want to set the field value if user presses enter or space in this control by mistake.
        // Since its a Rich Editor control, even a enter or space will cause to add empty div or br tags and we'll set the field value
        // as these empty tags and we'll see blank containers when we see them in WIT form (like History control).
        // This fix will try to remove all tags that can be added by mistake and replace them with "" and then see if there is any text left.
        // If all that the contents had was blank tags then we wont set the field value.
        // We check for contents.length for performance reason. String replace is not very performant call and we dont want to run it on long strings.
        // 100 seems to be long enough for text entered by mistake. We also dont want to not let user enter empty tags if he really wants to.
        if (value.length >= 100) {
            return false;
        }

        const wrapperElement = document.createElement("div");
        wrapperElement.innerHTML = value;
        return $(wrapperElement).text().trim().length === 0;
    }

    private onNewCommentChange = (newValue?: string): void => {
        const isEmpty = DiscussionEditor.isHtmlEmpty(newValue);
        
        this.setState({ isNewCommentEmpty: isEmpty });

        if (this.props.onChange) {
            this.props.onChange();
        }
    };

    private onNewCommentFocus = (): void => {
        this.setState({ isNewCommentActive: true });

        if (this.props.onFocus) {
            this.props.onFocus();
        }
    };

    private onNewCommentBlur = (): void => {
        const comment = this._control.htmlContent;
        this.setState({ isNewCommentActive: comment != undefined && comment !== "" });
    };

    public isEmpty(): boolean {
        return this._control && this._control.isEmpty();
    }

    public hasFocus(): boolean {
        return this._control && this._control.hasFocus();
    }

    public getValue(): string {
        return this._control && this._control.htmlContent;
    }

    public setValue(newValue: string, unsafe: boolean = false, focus: boolean = false): void {
        if (!this._control) {
            throw new Error("Expected the control to be instantiated");
        }

        if (newValue !== this.getValue()) {
            this._control.setContent(newValue, unsafe);
            const hasValue = newValue != undefined && newValue !== "";
            this.setState({ isNewCommentActive: hasValue });
        }

        if (focus) {
            this._control.focus(true /* scrollIntoView */);
            requestAnimationFrame(() => {
                const editableDiv = this._control && this._control.editableElement;
                if (editableDiv) {
                    // move caret to end
                    const selection = window.getSelection();
                    const range = document.createRange();
                    range.selectNodeContents(editableDiv);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            });
        }
    }

    public ready(callback: () => void): void {
        callback();
    }

    public selectText(): void {
        if (this._control) {
            this._control.selectAll();
        }
    }

    public checkModified(): void {
        if (this._control) {
            this._control.flushChanges();
        }
    }

    public isVisible(): boolean {
        return !this.state.isHidden;
    }

    public showElement(showControlButtons?: boolean): void {
        this.setState({ isHidden: false, areContolButtonsHidden: !showControlButtons });
    }

    public hideElement(): void {
        this.setState({ isHidden: true });
    }

    public setFullScreen(fullScreen: boolean): void {
        // for discussion, we want a decently sized fixed height, no auto grow and not the oob full screen fill
        // because we want saved comments to be visible below the editor
        this.setState({ height: fullScreen ? 180 : undefined });
    }

    public refreshCommandBar(): void {
        this._control && this._control.refreshCommandBar();
    }

    public focus(): void {
        this._control?.focus();
    }

    public get editableElement(): HTMLDivElement {
        return this._control && this._control.editableElement;
    }

    private _htmlEditorOnRef = (ref: HtmlEditor): void => {
        this._control = ref;
    };
}
